import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSalesReport } from "../../redux/apiCalls";
import { publicRequest } from "../../requestMethods";
import Select from "react-select";
import { FiPlus } from "react-icons/fi";
import styled, { keyframes } from "styled-components";
import { mobile, tablet } from "../../responsive";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";

const fadeIn = keyframes`
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
`;

const DashboardBg = styled.div`
    min-height: 100vh;
    background: #0b1220;
    color: #fff;
    padding-bottom: 3rem;
    position: relative;
    overflow: hidden;
    z-index: 1;
    width: 100%;
`;

const VideoBackground = styled.video`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: -2;
    opacity: 1;
    pointer-events: none;
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;

    @media (prefers-reduced-motion: reduce) {
        display: none;
    }
`;

const BackgroundOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.45);
    z-index: -1;
    pointer-events: none;
`;

const HudOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 1;
`;

const DashboardContainer = styled.div`
    margin: 0 auto;
    padding: 2.5rem 2rem 4rem;
    display: flex;
    flex-direction: column;
    animation: ${fadeIn} 1s ease;
    max-width: 1440px;
    width: min(100%, 1360px);
    box-sizing: border-box;

    ${tablet({
        padding: "2.25rem 1.75rem 3.5rem",
        width: "100%",
    })}

    ${mobile({
        padding: "2rem 1rem 3rem",
    })}
`;

const PageHeader = styled.div`
    margin-bottom: 2.5rem;
    text-align: center;

    ${tablet({
        marginBottom: "2.1rem",
    })}

    ${mobile({
        marginBottom: "1.8rem",
    })}
`;

const PageTitle = styled.h1`
    font-size: 2.75rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
    letter-spacing: 0.035em;
    background: linear-gradient(135deg, #ffffff 0%, #f3f4f6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    ${tablet({
        fontSize: "2.4rem",
    })}

    ${mobile({
        fontSize: "2.2rem",
    })}
`;

const PageSubtitle = styled.p`
    font-size: 1.15rem;
    color: rgba(209, 213, 219, 0.95);
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 500;
    letter-spacing: 0.02em;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

    ${tablet({
        fontSize: "1.05rem",
    })}

    ${mobile({
        fontSize: "1rem",
    })}
`;

const ChartSection = styled.section`
    background: rgba(17, 24, 39, 0.94);
    border-radius: 20px;
    box-shadow: 0 20px 45px rgba(15, 23, 42, 0.45);
    padding: 1.85rem 1.75rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin-bottom: 1.75rem;
    position: relative;
    z-index: 1;

    &.filter-section {
        z-index: 100;
    }

    ${tablet({
        padding: "1.6rem 1.4rem",
        marginBottom: "1.5rem",
    })}

    ${mobile({
        padding: "1.4rem 1.05rem",
        marginBottom: "1.25rem",
    })}
`;

const DesktopOnly = styled.div`
    ${mobile({
        display: "none",
    })}
`;

const MobileOnly = styled.div`
    display: none;

    ${mobile({
        display: "block",
    })}
`;

const ChartTitle = styled.h3`
    color: #ffffff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 700;
    font-size: 1.35rem;
    margin-bottom: 1.25rem;
    letter-spacing: 0.025em;
    position: relative;
    text-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);

    &::after {
        content: "";
        position: absolute;
        bottom: -0.6rem;
        left: 0;
        width: 3.25rem;
        height: 3px;
        background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0.9) 0%,
            rgba(255, 255, 255, 0.3) 100%
        );
        border-radius: 2px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.28);
    }
`;

const BlueChartTitle = styled.h3`
    color: #00eaff;
    font-family: "Inter", "Helvetica", Arial, sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    margin-bottom: 1rem;
    transition: color 1.2s ease;
    letter-spacing: 0.025em;
`;

const FiltersGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;

    ${tablet({
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: "1.1rem",
    })}

    ${mobile({
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    })}
`;

const FilterCard = styled.div`
    background: rgba(15, 23, 42, 0.85);
    border-radius: 14px;
    border: 1.5px solid rgba(59, 130, 246, 0.2);
    padding: 1.2rem 1.1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    ${tablet({
        padding: "1.15rem 1rem",
    })}

    ${mobile({
        padding: "1.05rem 0.95rem",
    })}
`;

const FilterLabel = styled.label`
    font-size: 0.8rem;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.85rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    margin-top: 1rem;

    ${tablet({
        justifyContent: "flex-start",
        marginTop: "1.15rem",
    })}

    ${mobile({
        flexDirection: "column",
        alignItems: "stretch",
        gap: "0.75rem",
        marginTop: "1.25rem",
    })}
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    border-radius: 9999px;
    padding: 0.65rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
    color: #ffffff;

    &.primary {
        background: linear-gradient(135deg, #ef4444, #f97316);
        box-shadow: 0 10px 25px rgba(239, 68, 68, 0.35);
        min-width: 180px;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(249, 115, 22, 0.4);
        }
    }
`;

const LuxuryTable = styled.div`
    background: linear-gradient(
        135deg,
        rgba(15, 23, 42, 0.88) 0%,
        rgba(30, 41, 59, 0.88) 100%
    );
    border-radius: 20px;
    box-shadow: 0 16px 40px rgba(15, 23, 42, 0.55);
    border: 1px solid rgba(59, 130, 246, 0.22);
    overflow-x: auto;
    padding-bottom: 0.25rem;
    -webkit-overflow-scrolling: touch;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    min-width: 720px;

    ${mobile({
        minWidth: "100%",
    })}
`;

const TableHead = styled.thead`
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.45), rgba(14, 165, 233, 0.5));

    ${mobile({
        display: "none",
    })}
`;

const TableHeaderCell = styled.th`
    padding: 0.85rem 1rem;
    text-align: left;
    font-size: 0.72rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 700;
    color: #0f172a;
    border-bottom: 1px solid rgba(148, 163, 184, 0.35);
`;

const TableBody = styled.tbody`
    background: transparent;
`;

const TableRow = styled.tr`
    transition: background 0.25s ease;

    &:nth-child(even) {
        background: rgba(30, 41, 59, 0.65);
    }

    &:hover {
        background: rgba(59, 130, 246, 0.25);
    }

    ${mobile({
        display: "block",
        marginBottom: "1rem",
        borderRadius: "12px",
        border: "1px solid rgba(148, 163, 184, 0.25)",
        overflow: "hidden",
        background: "rgba(30, 41, 59, 0.75)",
    })}
`;

const TableCell = styled.td`
    padding: 0.9rem 1rem;
    border-bottom: 1px solid rgba(148, 163, 184, 0.25);
    font-size: 0.92rem;
    color: #e2e8f0;

    ${mobile({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.85rem 1rem",
        fontSize: "0.9rem",
        "&::before": {
            content: "attr(data-label)",
            fontWeight: 600,
            color: "#9ca3af",
            marginRight: "1rem",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            fontSize: "0.7rem",
        },
        "&:last-child": {
            borderBottom: "none",
        },
    })}
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 3.5rem 1rem;
    color: rgba(226, 232, 240, 0.75);
`;

const MobileTrendList = styled.div`
    display: grid;
    gap: 0.75rem;
    max-height: 320px;
    overflow-y: auto;
    padding: 0.25rem 0.1rem 0.1rem;
    -webkit-overflow-scrolling: touch;
`;

const MobileTrendCard = styled.div`
    background: rgba(30, 41, 59, 0.9);
    border: 1px solid rgba(59, 130, 246, 0.4);
    border-radius: 12px;
    padding: 0.9rem 0.95rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    box-shadow: 0 10px 26px rgba(15, 23, 42, 0.45);
`;

const MobileTrendRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MobileTrendLabel = styled.span`
    font-size: 0.78rem;
    color: #9ca3af;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    font-weight: 600;
`;

const MobileTrendValue = styled.span`
    font-size: 0.95rem;
    font-weight: 700;
    color: #e5e7eb;
    text-align: right;
`;

const CompactTotal = styled.div`
    margin-top: 1rem;
    padding: 1rem 0.95rem;
    background: rgba(59, 130, 246, 0.12);
    border: 1px solid rgba(59, 130, 246, 0.35);
    border-radius: 12px;
    box-shadow: 0 10px 24px rgba(59, 130, 246, 0.2);
`;

const CompactTotalLabel = styled.div`
    font-size: 0.82rem;
    color: #94a3b8;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    margin-bottom: 0.35rem;
    font-weight: 700;
`;

const CompactTotalValue = styled.div`
    font-size: 1.4rem;
    font-weight: 800;
    color: #ffffff;
`;

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    gap: 1rem;
`;

const Spinner = styled.div`
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 999px;
    border: 3px solid rgba(148, 163, 184, 0.35);
    border-top-color: rgba(59, 130, 246, 0.85);
    animation: spin 0.8s linear infinite;

    @keyframes spin {
        0% {
            transform: rotate(0deg);
        }
        100% {
            transform: rotate(360deg);
        }
    }
`;

export default function SalesReport() {
    const dispatch = useDispatch();
    const salesReport = useSelector((state) => state.order.order);
    const currentUser = useSelector((state) => state.user.currentUser);
    const isFetching = useSelector((state) => state.order.isFetching);

    const TwoDecimalFormatter = new Intl.NumberFormat("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const principle = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.principle : null
    );
    const [isLoaded, setIsLoaded] = useState(false);
    const [inputs, setInputs] = useState({
        createdBy: currentUser._id,
        principle: manager
            ? principle
            : currentUser
            ? currentUser._id
            : undefined,
    });

    const getAdmin = async () => {
        let principleParam;

        if (currentUser) {
            if (currentUser.type === "outright") {
                principleParam = currentUser._id;
            } else if (currentUser.isSuperAdmin) {
                principleParam = undefined;
            } else {
                principleParam = currentUser.principle;
            }
        }

        const params = {
            isAdmin: true,
            type: "outright",
        };

        if (principleParam !== undefined) {
            params.principle = principleParam;
        }

        const res = await publicRequest.get("/users/allcashiers", {
            params,
        });
        setUsersData(res.data);
    };

    const handleUserFilter = (e) => {
        setInputs((prev) => {
            return { ...prev, createdBy: e.value };
        });
    };

    const handleResetFilters = () => {
        setInputs({
            createdBy: currentUser._id,
            principle: manager
                ? principle
                : currentUser
                ? currentUser._id
                : undefined,
        });
    };

    const [usersData, setUsersData] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const res = await getSalesReport(dispatch, inputs);
            if (res) {
                setIsLoaded(true);
            }
        };
        fetchData();
    }, [dispatch, inputs]);

    useEffect(() => {
        if (usersData[0] === undefined) {
            getAdmin();
        }
    }, [usersData]);

    const videoRef = useRef(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return undefined;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                video.pause();
                return;
            }
            video.play().catch(() => {});
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    const columns = [
        {
            Header: "Salesperson Name",
            accessor: "staff",
        },
        {
            Header: "Email",
            accessor: "email",
        },
        {
            Header: "Period",
            accessor: "date",
        },
        {
            Header: "Monthly Sales",
            accessor: "monthlySales",
        },
    ];

    const hasData = isLoaded && !isFetching && salesReport && salesReport.length > 0;

    // Prepare chart data from sales report
    const chartData = salesReport && salesReport.length > 0 
        ? salesReport.map((sale) => ({
              name: sale.date || "N/A",
              sales: sale.monthlySales || 0,
              staff: sale.staff || "Unknown"
          }))
        : [];

    // Calculate total sales for display
    const totalSales = chartData.reduce((sum, item) => sum + item.sales, 0);

    return (
        <DashboardBg>
            <VideoBackground
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
            >
                <source
                    src="https://ik.imagekit.io/dnddecpho/Inhome/animatedbg_Rx2Blqd5L.mp4?updatedAt=1746618975517"
                    type="video/mp4"
                />
            </VideoBackground>
            <BackgroundOverlay />
            <HudOverlay />
            <DashboardContainer>
                <PageHeader>
                    <PageTitle>Sales Report</PageTitle>
                    <PageSubtitle>
                        Track individual sales performance
                    </PageSubtitle>
                </PageHeader>

                <ChartSection className="filter-section">
                    <BlueChartTitle>Filters</BlueChartTitle>
                    <FiltersGrid>
                        <FilterCard>
                            <FilterLabel htmlFor="salesperson">Salesperson</FilterLabel>
                            <Select
                                className="borderBlack"
                                options={usersData.map((user, i) => {
                                    return {
                                        label: `${user.name}`,
                                        value: user._id,
                                    };
                                })}
                                placeholder="Select Salesperson"
                                onChange={handleUserFilter}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={{
                                    control: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: 'rgba(30, 41, 59, 0.85)',
                                        border: state.isFocused 
                                            ? '1px solid rgba(59, 130, 246, 0.75)' 
                                            : '1px solid rgba(148, 163, 184, 0.45)',
                                        borderRadius: '0.75rem',
                                        padding: '0.1rem',
                                        color: '#f8fafc',
                                        boxShadow: state.isFocused ? '0 0 0 4px rgba(59, 130, 246, 0.15)' : 'none',
                                        minHeight: '42px',
                                        '&:hover': {
                                            border: '1px solid rgba(59, 130, 246, 0.75)'
                                        }
                                    }),
                                    menuPortal: (provided) => ({
                                        ...provided,
                                        zIndex: 9999
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'rgba(17, 24, 39, 0.98)',
                                        border: '1px solid rgba(148, 163, 184, 0.45)',
                                        borderRadius: '0.75rem',
                                        overflow: 'hidden',
                                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                                        marginTop: '0.5rem',
                                        zIndex: 9999
                                    }),
                                    menuList: (provided) => ({
                                        ...provided,
                                        padding: '0.5rem',
                                        maxHeight: '250px'
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: state.isSelected 
                                            ? 'rgba(59, 130, 246, 0.45)' 
                                            : state.isFocused 
                                            ? 'rgba(59, 130, 246, 0.25)' 
                                            : 'transparent',
                                        color: '#e2e8f0',
                                        cursor: 'pointer',
                                        padding: '0.75rem 1rem',
                                        borderRadius: '0.5rem',
                                        transition: 'all 0.2s ease',
                                        '&:active': {
                                            backgroundColor: 'rgba(59, 130, 246, 0.45)'
                                        }
                                    }),
                                    singleValue: (provided) => ({
                                        ...provided,
                                        color: '#f8fafc'
                                    }),
                                    input: (provided) => ({
                                        ...provided,
                                        color: '#f8fafc'
                                    }),
                                    placeholder: (provided) => ({
                                        ...provided,
                                        color: '#9ca3af'
                                    }),
                                    indicatorSeparator: (provided) => ({
                                        ...provided,
                                        backgroundColor: 'rgba(148, 163, 184, 0.3)'
                                    }),
                                    dropdownIndicator: (provided) => ({
                                        ...provided,
                                        color: '#9ca3af',
                                        '&:hover': {
                                            color: '#f8fafc'
                                        }
                                    }),
                                    clearIndicator: (provided) => ({
                                        ...provided,
                                        color: '#9ca3af',
                                        '&:hover': {
                                            color: '#f8fafc'
                                        }
                                    })
                                }}
                            />
                        </FilterCard>
                    </FiltersGrid>
                    <ActionButtons>
                        <ActionButton type="button" className="primary" onClick={handleResetFilters}>
                            <FiPlus />
                            Reset Filters
                        </ActionButton>
                    </ActionButtons>
                </ChartSection>

                {/* Sales Trend Chart */}
                {hasData && (
                    <ChartSection>
                        <ChartTitle>Sales Trend</ChartTitle>
                        <DesktopOnly>
                            <div style={{ padding: "1.5rem 0.5rem" }}>
                                <ResponsiveContainer width="100%" height={350}>
                                    <LineChart
                                        data={chartData}
                                        margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                                    >
                                        <defs>
                                            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
                                        <XAxis 
                                            dataKey="name" 
                                            stroke="#94a3b8"
                                            style={{ fontSize: '0.85rem', fontFamily: 'Inter, Helvetica, Arial, sans-serif' }}
                                        />
                                        <YAxis 
                                            stroke="#94a3b8"
                                            style={{ fontSize: '0.85rem', fontFamily: 'Inter, Helvetica, Arial, sans-serif' }}
                                            tickFormatter={(value) => `${process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}${(value / 1000).toFixed(0)}k`}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'rgba(17, 24, 39, 0.95)',
                                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                                borderRadius: '0.75rem',
                                                color: '#e2e8f0',
                                                fontFamily: 'Inter, Helvetica, Arial, sans-serif',
                                                fontSize: '0.9rem',
                                                padding: '0.75rem 1rem'
                                            }}
                                            formatter={(value, name, props) => [
                                                `${process.env.REACT_APP_COUNTRY === "SINGAPORE" || props.payload.staff === "HAOFA Tourbillon Singapore" ? "$" : "RM"}${TwoDecimalFormatter.format(value)}`,
                                                'Sales'
                                            ]}
                                            labelFormatter={(label) => `Period: ${label}`}
                                        />
                                        <Legend 
                                            wrapperStyle={{
                                                paddingTop: '1rem',
                                                fontFamily: 'Inter, Helvetica, Arial, sans-serif',
                                                fontSize: '0.9rem',
                                                color: '#e2e8f0'
                                            }}
                                        />
                                        <Line 
                                            type="monotone" 
                                            dataKey="sales" 
                                            stroke="#3b82f6" 
                                            strokeWidth={3}
                                            dot={{ fill: '#3b82f6', r: 5 }}
                                            activeDot={{ r: 7, fill: '#60a5fa' }}
                                            name="Monthly Sales"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                                <div style={{ 
                                    textAlign: 'center', 
                                    marginTop: '1.5rem',
                                    padding: '1rem',
                                    background: 'rgba(59, 130, 246, 0.1)',
                                    borderRadius: '0.75rem',
                                    border: '1px solid rgba(59, 130, 246, 0.3)'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.85rem', 
                                        color: '#94a3b8',
                                        marginBottom: '0.5rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontWeight: 600
                                    }}>
                                        Total Sales
                                    </div>
                                    <div style={{ 
                                        fontSize: '1.75rem', 
                                        fontWeight: 700,
                                        color: '#ffffff',
                                        fontFamily: 'Inter, Helvetica, Arial, sans-serif'
                                    }}>
                                        {process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                        {TwoDecimalFormatter.format(totalSales)}
                                    </div>
                                </div>
                            </div>
                        </DesktopOnly>

                        <MobileOnly>
                            <MobileTrendList>
                                {chartData.map((item, index) => (
                                    <MobileTrendCard key={`${item.name || "period"}-${index}`}>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Period</MobileTrendLabel>
                                            <MobileTrendValue>{item.name || "N/A"}</MobileTrendValue>
                                        </MobileTrendRow>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Salesperson</MobileTrendLabel>
                                            <MobileTrendValue>{item.staff || "Unknown"}</MobileTrendValue>
                                        </MobileTrendRow>
                                        <MobileTrendRow>
                                            <MobileTrendLabel>Monthly Sales</MobileTrendLabel>
                                            <MobileTrendValue>
                                                {item.staff === "HAOFA Tourbillon Singapore" || process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                                {TwoDecimalFormatter.format(item.sales || 0)}
                                            </MobileTrendValue>
                                        </MobileTrendRow>
                                    </MobileTrendCard>
                                ))}
                            </MobileTrendList>
                            <CompactTotal>
                                <CompactTotalLabel>Total Sales</CompactTotalLabel>
                                <CompactTotalValue>
                                    {process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                    {TwoDecimalFormatter.format(totalSales)}
                                </CompactTotalValue>
                            </CompactTotal>
                        </MobileOnly>
                    </ChartSection>
                )}

                <ChartSection>
                    <ChartTitle>Sales Breakdown by Staff</ChartTitle>
                    <LuxuryTable>
                        {hasData ? (
                            <StyledTable>
                                <TableHead>
                                    <tr>
                                        {columns.map((column) => (
                                            <TableHeaderCell
                                                key={column.accessor || column.Header}
                                            >
                                                {column.Header}
                                            </TableHeaderCell>
                                        ))}
                                    </tr>
                                </TableHead>
                                <TableBody>
                                    {salesReport.map((sale, index) => (
                                        <TableRow key={`${sale.staff || "staff"}-${index}`}>
                                            <TableCell data-label="Salesperson Name">
                                                <div className="text-sm font-medium">
                                                    {sale.staff || "Unknown User"}
                                                </div>
                                            </TableCell>
                                            <TableCell data-label="Email">
                                                <div className="text-sm">
                                                    {sale.email || "N/A"}
                                                </div>
                                            </TableCell>
                                            <TableCell data-label="Period">
                                                <div className="text-sm">
                                                    {sale.date || "N/A"}
                                                </div>
                                            </TableCell>
                                            <TableCell data-label="Monthly Sales">
                                                <div className="text-sm font-bold">
                                                    {sale.staff === "HAOFA Tourbillon Singapore" || process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM"}
                                                    {TwoDecimalFormatter.format(sale.monthlySales || 0)}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </StyledTable>
                        ) : isLoaded && !isFetching ? (
                            <EmptyState>
                                <div style={{ fontSize: "1.15rem", fontWeight: 600, marginBottom: "0.6rem" }}>
                                    No sales data found
                                </div>
                                <div style={{ fontSize: "0.95rem", maxWidth: "520px", margin: "0 auto" }}>
                                    Try selecting a different salesperson or check if there are any sales for this period.
                                </div>
                            </EmptyState>
                        ) : (
                            <LoaderWrapper>
                                <Spinner />
                                <span>Loading sales data...</span>
                            </LoaderWrapper>
                        )}
                    </LuxuryTable>
                </ChartSection>
            </DashboardContainer>
        </DashboardBg>
    );
}
